import { http } from './request'
import type { RequestConfig } from './types'

/** 部门树节点（由 org/page 扁平列表按 parentId 组装；树接口还含 regionLevel） */
export interface DepartmentApiNode {
  id: string
  name: string
  /** 与后端一致：省级 / 市级 / 区县级 … */
  regionLevel?: string
  /** 行政区划末级代码（若树接口返回，用于回填地区筛选） */
  administrativeCode?: string
  children?: DepartmentApiNode[]
}

/** 地区下部门分页列表（左侧选中地区后） */
export interface OrgPageParams {
  /** 行政区划代码（9 位国标；可传更短数字，请求前会规范为 9 位） */
  administrativeCode: string
  /** 部门名称关键词 */
  searchKey?: string
  /** 是否包含下级区划；默认 true（当前地区及下级）；false 为仅本级 */
  includeSubRegion?: boolean
  current: number
  size: number
}

export interface OrgPageRecord {
  id: string
  name: string
  /** 层级（分类） */
  regionLevel: string | number
  sortCode: string | number
  /** 上级部门 id（若分页接口返回） */
  parentId?: string
  /** 所属行政区划末级代码（若分页接口返回，编辑回显地区上下文） */
  administrativeCode?: string
}

interface OrgPageRawData {
  records?: unknown[]
  list?: unknown[]
  total?: number
}

function pickStr(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k]
    if (v != null && v !== '') return String(v)
  }
  return ''
}

function rawToOrgPageRecord(raw: unknown, index: number): OrgPageRecord | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const id = pickStr(o, 'id', 'orgId', 'deptId') || `org-${index}`
  const name = pickStr(o, 'name')
  if (!name) return null
  const rl = o.regionLevel
  const sc = o.sortCode
  const pidRaw = o.parentId ?? o.parentOrgId ?? o.pid
  const parentId = pidRaw != null && pidRaw !== '' ? String(pidRaw as string | number) : undefined
  const admRaw = o.administrativeCode ?? o.areaId ?? o.regionCode
  const administrativeCode =
    admRaw != null && admRaw !== '' ? String(admRaw as string | number) : undefined
  return {
    id,
    name,
    regionLevel: rl !== undefined && rl !== null ? (rl as string | number) : '—',
    sortCode: sc !== undefined && sc !== null ? (sc as string | number) : '—',
    ...(parentId ? { parentId } : {}),
    ...(administrativeCode ? { administrativeCode } : {}),
  }
}

function pickTreeStr(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k]
    if (v != null && v !== '') return String(v)
  }
  return ''
}

/** 将 org/tree 原始 JSON 规范为 DepartmentApiNode（兼容 snake_case、多套 name 字段、多包一层 data） */
function normalizeOrgTreeNode(raw: unknown): DepartmentApiNode | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const id = pickTreeStr(o, 'id', 'orgId', 'deptId', 'key')
  const name = pickTreeStr(o, 'name', 'orgName', 'deptName', 'title', 'label')
  if (!id || !name) return null
  const regionLevel = pickTreeStr(o, 'regionLevel', 'region_level', 'orgLevel', 'org_level')
  const administrativeCode = pickTreeStr(
    o,
    'administrativeCode',
    'administrative_code',
    'areaId',
    'regionCode',
  )
  const childrenRaw = o.children ?? o.childList ?? o.nodes
  let children: DepartmentApiNode[] | undefined
  if (Array.isArray(childrenRaw) && childrenRaw.length) {
    children = childrenRaw
      .map(normalizeOrgTreeNode)
      .filter((x): x is DepartmentApiNode => x != null)
    if (!children.length) children = undefined
  }
  const node: DepartmentApiNode = {
    id,
    name,
    ...(administrativeCode ? { administrativeCode } : {}),
    ...(children ? { children } : {}),
  }
  if (regionLevel) node.regionLevel = regionLevel
  return node
}

function normalizeOrgTreeResponse(raw: unknown): DepartmentApiNode[] {
  if (raw == null) return []
  if (Array.isArray(raw)) {
    return raw.map(normalizeOrgTreeNode).filter((x): x is DepartmentApiNode => x != null)
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>
    if (Array.isArray(o.data)) return normalizeOrgTreeResponse(o.data)
    const one = normalizeOrgTreeNode(raw)
    return one ? [one] : []
  }
  return []
}

/**
 * 上级部门树（新增/编辑弹窗）
 * GET `/biz/overseas/sysuser/org/tree`，query：`administrativeCode` 为当前所选区划末级代码（完整或 9 位国标），后端会推导 like 前缀；
 * 不要只传省级前两位，否则会筛出全省组织。
 */
export async function fetchOrgTree(administrativeCode: string): Promise<DepartmentApiNode[]> {
  const code = administrativeCode.trim()
  if (!code) return []
  try {
    const raw = await http.get<unknown>('/biz/overseas/sysuser/org/tree', {
      params: { administrativeCode: code },
    })
    return normalizeOrgTreeResponse(raw)
  } catch {
    return []
  }
}

/**
 * 案件分配专用：按地区码拉取可分配组织树（结构与 org/tree 一致；独立接口避免与 `/org/page` 等混用）。
 * GET `/overseas/case/assign/orgTree`，query：`administrativeCode` 为行政区划编码（与分配提交字段 areaId 一致）
 */
export async function fetchCaseAssignOrgTree(
  areaId: string,
  flowDirection?: 'UP' | 'DOWN',
): Promise<DepartmentApiNode[]> {
  const code = areaId.trim()
  if (!code) return []
  try {
    const raw = await http.get<unknown>('/overseas/case/assign/orgTree', {
      params: {
        administrativeCode: code,
        ...(flowDirection ? { flowDirection } : {}),
      },
    })
    return normalizeOrgTreeResponse(raw)
  } catch {
    return []
  }
}

/** 案件管理列表筛选：本机构及下级，走案件域接口，不依赖用户管理权限 */
export async function fetchCaseManageOrgTree(): Promise<DepartmentApiNode[]> {
  try {
    const raw = await http.get<unknown>('/overseas/case/orgFilterTree')
    return normalizeOrgTreeResponse(raw)
  } catch {
    return []
  }
}

/** 用户管理弹窗：仅当前用户本机构及下级组织树（不传区划码） */
export async function fetchScopedOrgTree(): Promise<DepartmentApiNode[]> {
  try {
    const raw = await http.get<unknown>('/biz/overseas/sysuser/org/tree')
    return normalizeOrgTreeResponse(raw)
  } catch {
    return []
  }
}

/**
 * 系统组织树（SysOrgController），与 GET `/sys/org/tree` 一致。
 * 开发环境下 `VITE_API_BASE_URL=/api` 时实际请求为 `/api/sys/org/tree`。
 */
export async function fetchSysOrgTree(): Promise<DepartmentApiNode[]> {
  const raw = await http.get<unknown>('/sys/org/tree')
  return normalizeOrgTreeResponse(raw)
}

/** 协同派发选部门：全库组织，含同级其他部门，不按本机构裁剪 */
export async function fetchCaseCollabOrgTree(): Promise<DepartmentApiNode[]> {
  try {
    const raw = await http.get<unknown>('/overseas/case/collab/orgTree')
    return normalizeOrgTreeResponse(raw)
  } catch {
    return []
  }
}

export interface SysOrgDetail {
  id: string
  name?: string
  administrativeCode?: string
}

export async function fetchSysOrgDetail(
  id: string,
  req?: Pick<RequestConfig, 'skipErrorMessage'>,
): Promise<SysOrgDetail | null> {
  const oid = id?.trim()
  if (!oid) return null
  const raw = await http.get<unknown>('/sys/org/detail', {
    params: { id: oid },
    skipErrorMessage: req?.skipErrorMessage,
  })
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const out: SysOrgDetail = { id: String(o.id ?? oid) }
  if (o.name != null) out.name = String(o.name)
  if (o.administrativeCode != null) out.administrativeCode = String(o.administrativeCode)
  return out
}

export interface SysOrgUserSelectorRecord {
  id: string
  name: string
  account?: string
  orgId?: string
}

export async function fetchSysOrgUserSelectorPage(params: {
  current: number
  size: number
  orgId?: string
  searchKey?: string
}): Promise<{ records: SysOrgUserSelectorRecord[]; total: number }> {
  const q: Record<string, string | number> = {
    current: params.current,
    size: params.size,
  }
  const oid = params.orgId?.trim()
  if (oid) q.orgId = oid
  const sk = params.searchKey?.trim()
  if (sk) q.searchKey = sk
  const raw = await http.get<{ records?: unknown[]; total?: number }>('/sys/org/userSelector', {
    params: q,
  })
  const records = Array.isArray(raw.records) ? raw.records : []
  const list = records
    .map((r, i) => {
      if (!r || typeof r !== 'object') return null
      const o = r as Record<string, unknown>
      const id = o.id != null ? String(o.id) : `row-${i}`
      const name = o.name != null ? String(o.name) : ''
      if (!name) return null
      const rec: SysOrgUserSelectorRecord = { id, name }
      if (o.account != null) rec.account = String(o.account)
      if (o.orgId != null) rec.orgId = String(o.orgId)
      return rec
    })
    .filter((x): x is SysOrgUserSelectorRecord => x != null)
  const total = typeof raw.total === 'number' ? raw.total : list.length
  return { records: list, total }
}

/**
 * 可选上级组织树：后端按当前区划的上一级匹配（市→省、县→市、乡→县），组织 administrativeCode 须等于该上级 9 位码。
 * GET `/biz/overseas/sysuser/org/tree/provinceParentsForCity`
 */
export async function fetchOrgTreeProvinceParentsForCity(
  administrativeCode: string,
): Promise<DepartmentApiNode[]> {
  const code = administrativeCode.trim()
  if (!code) return []
  try {
    const raw = await http.get<unknown>('/biz/overseas/sysuser/org/tree/provinceParentsForCity', {
      params: { administrativeCode: code },
    })
    return normalizeOrgTreeResponse(raw)
  } catch {
    return []
  }
}

/** 与后端 `formatNineDigitAdministrativeCode` 一致：抽数字、超长截断、不足右侧补 0 至 9 位 */
export function normalizeAdministrativeCodeToNine(raw: string): string {
  const d = raw.replace(/\D/g, '')
  if (!d) return ''
  if (d.length >= 9) return d.slice(0, 9)
  return d.padEnd(9, '0')
}

/**
 * 部门分页（选中地区后列表）
 * GET `/biz/overseas/sysuser/org/page`，query：`administrativeCode`（9 位国标）、`searchKey`、`current`、`size`
 */
export async function fetchOrgPage(
  params: OrgPageParams,
): Promise<{ list: OrgPageRecord[]; total: number }> {
  const code = normalizeAdministrativeCodeToNine(params.administrativeCode.trim())
  if (!code) return { list: [], total: 0 }
  const raw = await http.get<OrgPageRawData>('/biz/overseas/sysuser/org/page', {
    params: {
      administrativeCode: code,
      searchKey: params.searchKey?.trim() || undefined,
      includeSubRegion: params.includeSubRegion !== false,
      current: params.current,
      size: params.size,
    },
  })
  const records = Array.isArray(raw.records) ? raw.records : Array.isArray(raw.list) ? raw.list : []
  const list = records
    .map((r, i) => rawToOrgPageRecord(r, i))
    .filter((x): x is OrgPageRecord => x != null)
  const total = typeof raw.total === 'number' ? raw.total : list.length
  return { list, total }
}

/**
 * 将组织分页记录的扁平列表按 parentId 组装为树（不再使用 `/system/dept/tree`）。
 * parentId 为空或 `"0"` 视为根节点。
 */
export function orgPageListToDepartmentNodes(rows: OrgPageRecord[]): DepartmentApiNode[] {
  if (!rows.length) return []
  const map = new Map<string, DepartmentApiNode>()
  for (const r of rows) {
    map.set(r.id, { id: r.id, name: r.name, children: [] })
  }
  const roots: DepartmentApiNode[] = []
  for (const r of rows) {
    const node = map.get(r.id)
    if (!node) continue
    const pid = r.parentId
    if (pid == null || pid === '' || pid === '0') {
      roots.push(node)
      continue
    }
    const parent = map.get(pid)
    if (parent) {
      if (!parent.children) parent.children = []
      parent.children.push(node)
    } else {
      roots.push(node)
    }
  }
  return roots
}

/**
 * 组织新增/编辑请求体
 * 与 `POST /biz/overseas/sysuser/org/add`、`POST /biz/overseas/sysuser/org/edit` 一致
 */
export interface OrgAddPayload {
  /** 部门层级：1 省 2 市 3 区/县 */
  regionLevel: 1 | 2 | 3
  /** 所属地区：行政区划末级代码 */
  administrativeCode: string
  /** 所属上级部门 id；省级固定为 `"A01"`（组织虚拟根，勿传 `"0"`） */
  parentId: string
  /** 部门名称 */
  name: string
  /** 排序，选填 */
  sortCode?: number
}

export type OrgEditPayload = OrgAddPayload & { id: string }

export async function createDepartment(payload: OrgAddPayload): Promise<void> {
  await http.post<void>('/biz/overseas/sysuser/org/add', payload)
}

export async function updateOrg(payload: OrgEditPayload): Promise<void> {
  await http.post<void>('/biz/overseas/sysuser/org/edit', payload)
}

export interface UpdateDepartmentPayload {
  name: string
}

export async function updateDepartment(
  departmentId: string,
  payload: UpdateDepartmentPayload,
): Promise<void> {
  await http.post<void>(`/system/dept/${encodeURIComponent(departmentId)}`, payload)
}

export async function deleteDepartment(departmentId: string): Promise<void> {
  await http.post<void>(`/system/dept/${encodeURIComponent(departmentId)}`)
}

/**
 * 删除组织（单条传单元素数组即可）
 * POST `/biz/overseas/sysuser/org/delete`，body：`{ id: string[] }`
 */
export async function deleteOrg(ids: string[]): Promise<void> {
  const id = ids.map((x) => String(x).trim()).filter(Boolean)
  if (!id.length) return
  await http.post<void>('/biz/overseas/sysuser/org/delete', { id })
}
